<script lang="ts">
  // Demo-only autologin route. Reached via `#/demo` (linked from the public
  // website). Auto-logs in with the guarded demo student account and forwards
  // to the class view, which renders the Test-Modus badge + a placeholder
  // stream.
  //
  // This route is INTENTIONALLY demo-only: it sends the fixed demo student
  // credentials. The backend only accepts them when the operator has enabled
  // the demo (demo.enabled) — which is when the demo account is seeded. With
  // the demo off, login fails and we show a friendly message instead of
  // leaking anything.
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import { login, ApiError } from "../lib/api";
  import { setUser } from "../lib/session.svelte";
  import { setLocale, t, _ } from "../lib/i18n";

  // Fixed demo student credentials (see bootstrapDemo in backend db.ts).
  const DEMO_EMAIL = "demo@demo.local";
  const DEMO_PASSWORD = "demo";

  let error: string | null = null;

  onMount(async () => {
    try {
      const res = await login({ email: DEMO_EMAIL, password: DEMO_PASSWORD });
      setUser({
        email: res.email,
        display_name: res.display_name,
        roles: res.roles,
        language: res.language,
        demo: res.demo === true,
        // Demo principal is seeded with must_change_password=0, so this is
        // false and the forced-change guard never fires for the demo login.
        must_change_password: res.must_change_password === true,
      });
      setLocale(res.language);
      push("/class");
    } catch (err) {
      error =
        err instanceof ApiError && err.status === 401
          ? t("demo.failed")
          : err instanceof Error
            ? err.message
            : t("demo.failed");
    }
  });
</script>

<section class="demo">
  <div class="card">
    <span class="badge" data-testid="demo-badge">{$_("testMode.badge")}</span>
    {#if error}
      <p class="error" data-testid="demo-error">{error}</p>
    {:else}
      <p class="sub" data-testid="demo-loading">{$_("demo.signingIn")}</p>
    {/if}
  </div>
</section>

<style>
  .demo {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 1rem;
    /* iOS viewport-fit=cover: keep the card out from under the status bar and
       home indicator. env() is 0 elsewhere, so 1rem is preserved. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  .card {
    width: 100%;
    max-width: 360px;
    display: grid;
    gap: 0.75rem;
    justify-items: center;
    background: #111827;
    border: 1px solid #374151;
    border-radius: 12px;
    padding: 1.5rem;
    text-align: center;
  }
  .badge {
    padding: 0.25rem 0.75rem;
    font-weight: 700;
    font-size: 0.8rem;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #04221a;
    background: #fbbf24;
    border-radius: 999px;
  }
  .sub {
    margin: 0;
    color: #9ca3af;
  }
  .error {
    margin: 0;
    color: #fca5a5;
  }
</style>
